/**
 * Draft auto-save configuration
 */
export interface DraftConfig {
  autoSaveIntervalMs: number;
  maxDraftAge: number;
}

/**
 * Auto-save draft data
 */
interface DraftData {
  reviewId: number;
  responseId?: number;
  itemId: number;
  data: any;
  lastSaved: Date;
  mutationAt: Date;
  isDirty: boolean;
}

type DraftSaver = (
  reviewId: number,
  itemId: number,
  data: any,
  options?: { mutationTimestamp?: Date }
) => Promise<unknown>;

const DEFAULT_DRAFT_CONFIG: DraftConfig = {
  autoSaveIntervalMs: 30000,
  maxDraftAge: 24,
};

export class OfflineDraftManager {
  private config: DraftConfig = DEFAULT_DRAFT_CONFIG;
  private autoSaveTimers: Map<string, NodeJS.Timeout> = new Map();
  private draftDataMap: Map<string, DraftData> = new Map();

  constructor(private readonly saveDraftResponse: DraftSaver) {}

  configure(config: Partial<DraftConfig>): void {
    this.config = { ...this.config, ...config };
  }

  trackDraftUpdate(
    draftKey: string,
    reviewId: number,
    itemId: number,
    data: any,
    responseId?: number
  ): void {
    const existingDraft = this.draftDataMap.get(draftKey);

    this.draftDataMap.set(draftKey, {
      reviewId,
      responseId,
      itemId,
      data: existingDraft ? { ...existingDraft.data, ...data } : data,
      lastSaved: existingDraft?.lastSaved || new Date(0),
      mutationAt: new Date(),
      isDirty: true,
    });

    this.scheduleAutoSave(draftKey);
  }

  async saveAllDrafts(): Promise<void> {
    const dirtyDrafts = Array.from(this.draftDataMap.entries())
      .filter(([, draft]) => draft.isDirty);

    if (dirtyDrafts.length === 0) {
      return;
    }

    const savePromises = dirtyDrafts.map(([draftKey]) => this.saveDraft(draftKey));
    await Promise.allSettled(savePromises);
  }

  cleanupOldDrafts(): void {
    try {
      const maxAge = this.config.maxDraftAge * 60 * 60 * 1000;
      const cutoffTime = new Date(Date.now() - maxAge);

      for (const [draftKey, draft] of this.draftDataMap.entries()) {
        if (draft.lastSaved < cutoffTime) {
          this.draftDataMap.delete(draftKey);
          this.clearTimer(draftKey);
        }
      }
    } catch (error) {
      console.warn('[OfflineDraftManager] Error cleaning up old drafts:', error);
    }
  }

  cleanup(): void {
    for (const timer of this.autoSaveTimers.values()) {
      clearTimeout(timer);
    }

    this.autoSaveTimers.clear();
    this.draftDataMap.clear();
  }

  get timerCount(): number {
    return this.autoSaveTimers.size;
  }

  get draftCount(): number {
    return this.draftDataMap.size;
  }

  private scheduleAutoSave(draftKey: string): void {
    this.clearTimer(draftKey);

    const timer = setTimeout(() => {
      this.saveDraft(draftKey).catch(error => {
        console.warn('[OfflineDraftManager] Auto-save failed:', error);
      });
    }, this.config.autoSaveIntervalMs);

    this.autoSaveTimers.set(draftKey, timer);
  }

  private async saveDraft(draftKey: string): Promise<void> {
    const draftData = this.draftDataMap.get(draftKey);
    if (!draftData || !draftData.isDirty) {
      return;
    }

    try {
      await this.saveDraftResponse(draftData.reviewId, draftData.itemId, draftData.data, {
        mutationTimestamp: draftData.mutationAt,
      });

      draftData.lastSaved = new Date();
      draftData.isDirty = false;
      this.draftDataMap.set(draftKey, draftData);
    } catch (error) {
      console.warn('[OfflineDraftManager] Error auto-saving draft:', error);
    }
  }

  private clearTimer(draftKey: string): void {
    const existingTimer = this.autoSaveTimers.get(draftKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.autoSaveTimers.delete(draftKey);
    }
  }
}
